'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui'
import { Card, CardContent } from '@/components/ui'
import { Crown } from 'lucide-react'
import { gojuonData, dakutenData, type Kana } from '@/data/gojuon'
import { playGojuonAudio } from '@/utils/gojuonAudio'
import { KanaStrokeAnimation } from '@/components/gojuon/KanaStrokeAnimation'
import { PlanSelectionModal } from '@/components/subscription/PlanSelectionModal'

// 試用版：使用 localStorage 保存進度
// 與正式版邏輯一致，支援完整的數據結構（包括 fail 和 lastUpdated）
const useTrialGojuonProgress = () => {
  const [progress, setProgress] = useState<{
    hiragana: Record<string, { learned: boolean; practiced: boolean; written: boolean; failed?: boolean; lastUpdated?: number }>
    katakana: Record<string, { learned: boolean; practiced: boolean; written: boolean; failed?: boolean; lastUpdated?: number }>
  }>({
    hiragana: {},
    katakana: {}
  })

  useEffect(() => {
    const saved = localStorage.getItem('trial_gojuon_progress')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        const normalized: typeof progress = {
          hiragana: {},
          katakana: {}
        }
        
        if (parsed.hiragana) {
          Object.keys(parsed.hiragana).forEach(key => {
            normalized.hiragana[key] = {
              learned: parsed.hiragana[key].learned || false,
              practiced: parsed.hiragana[key].practiced || false,
              written: parsed.hiragana[key].written || false,
              failed: parsed.hiragana[key].failed || false,
              lastUpdated: parsed.hiragana[key].lastUpdated || 0
            }
          })
        }
        
        if (parsed.katakana) {
          Object.keys(parsed.katakana).forEach(key => {
            normalized.katakana[key] = {
              learned: parsed.katakana[key].learned || false,
              practiced: parsed.katakana[key].practiced || false,
              written: parsed.katakana[key].written || false,
              failed: parsed.katakana[key].failed || false,
              lastUpdated: parsed.katakana[key].lastUpdated || 0
            }
          })
        }
        
        setProgress(normalized)
      } catch (e) {
        console.error('Failed to load trial gojuon progress:', e)
      }
    }
  }, [])

  useEffect(() => {
    if (Object.keys(progress.hiragana).length > 0 || Object.keys(progress.katakana).length > 0) {
      try {
        localStorage.setItem('trial_gojuon_progress', JSON.stringify(progress))
      } catch (e) {
        console.error('Failed to save trial gojuon progress:', e)
      }
    }
  }, [progress])

  const updateProgress = (
    kanaKey: string,
    type: 'learn' | 'practice' | 'write' | 'fail',
    mode: 'hiragana' | 'katakana'
  ) => {
    setProgress(prev => {
      const newProgress = { ...prev }
      const prevModeMap = newProgress[mode] || {}
      const current = prevModeMap[kanaKey] || {
        learned: false,
        practiced: false,
        written: false,
        failed: false,
        lastUpdated: 0
      }

      const updated = {
        ...current,
        [type === 'learn' ? 'learned' : type === 'practice' ? 'practiced' : type === 'write' ? 'written' : 'failed']: true,
        ...(type === 'practice' ? { failed: false } : {}),
        lastUpdated: Date.now()
      }

      return {
        ...newProgress,
        [mode]: {
          ...prevModeMap,
          [kanaKey]: updated
        }
      }
    })
  }

  return { progress, updateProgress }
}

const GojuonWriteTrialPage: React.FC = () => {
  const router = useRouter()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const contextRef = useRef<CanvasRenderingContext2D | null>(null)
  const isDrawingRef = useRef(false)
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isHiragana, setIsHiragana] = useState(true)
  const [currentKana, setCurrentKana] = useState<Kana | null>(null)
  const [completed, setCompleted] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const { progress, updateProgress } = useTrialGojuonProgress()
  // 追蹤當前播放的音檔，用於防止多重音軌
  const currentAudioRef = useRef<HTMLAudioElement | null>(null)

  const openPlanModal = () => {
    setShowPlanModal(true)
  }

  const closePlanModal = () => {
    setShowPlanModal(false)
  }

  // 同步 ref 和 state
  useEffect(() => {
    contextRef.current = context
  }, [context])

  useEffect(() => {
    isDrawingRef.current = isDrawing
  }, [isDrawing])

  // 響應式 Canvas 尺寸
  const [canvasSize, setCanvasSize] = useState(300)

  useEffect(() => {
    const updateCanvasSize = () => {
      // 根據視窗寬度動態調整 Canvas 尺寸
      const maxWidth = Math.min(window.innerWidth - 64, 400) // 減去 padding
      const size = Math.max(250, Math.min(maxWidth, 400)) // 最小 250px，最大 400px
      setCanvasSize(size)
    }

    updateCanvasSize()
    window.addEventListener('resize', updateCanvasSize)
    return () => window.removeEventListener('resize', updateCanvasSize)
  }, [])

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current
      // 設置 Canvas 實際尺寸
      canvas.width = canvasSize
      canvas.height = canvasSize
      
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 8
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        contextRef.current = ctx
        setContext(ctx)
      }

      // 使用原生事件監聽器，設置 passive: false 以允許 preventDefault
      const handleTouchStart = (e: TouchEvent) => {
        e.preventDefault()
        const ctx = contextRef.current
        if (!ctx || !canvas) return
        
        const rect = canvas.getBoundingClientRect()
        const touch = e.touches[0]
        const x = touch.clientX - rect.left
        const y = touch.clientY - rect.top
        
        isDrawingRef.current = true
        setIsDrawing(true)
        ctx.beginPath()
        ctx.moveTo(x, y)
      }

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        const ctx = contextRef.current
        if (!isDrawingRef.current || !ctx || !canvas) return
        
        const rect = canvas.getBoundingClientRect()
        const touch = e.touches[0]
        const x = touch.clientX - rect.left
        const y = touch.clientY - rect.top
        
        ctx.lineTo(x, y)
        ctx.stroke()
      }

      const handleTouchEnd = (e: TouchEvent) => {
        e.preventDefault()
        isDrawingRef.current = false
        setIsDrawing(false)
      }

      // 添加事件監聽器，設置 passive: false
      canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
      canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
      canvas.addEventListener('touchend', handleTouchEnd, { passive: false })
      canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false })

      // 清理函數
      return () => {
        canvas.removeEventListener('touchstart', handleTouchStart)
        canvas.removeEventListener('touchmove', handleTouchMove)
        canvas.removeEventListener('touchend', handleTouchEnd)
        canvas.removeEventListener('touchcancel', handleTouchEnd)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKana, canvasSize]) // canvasSize 在 useEffect 內部使用，需要依賴

  // 選擇下一個假名
  // 注意：只使用 gojuonData（基本 50 音），不包含 dakutenData（濁音半濁音）
  // 因為濁音半濁音沒有對應的 GIF 筆順動畫文件
  const selectNextKana = useCallback(async (playSound = false) => {
    const allKana = Object.values(gojuonData).flat() // 只使用基本 50 音
    const unmastered = allKana.filter(kana => !(progress[isHiragana ? 'hiragana' : 'katakana'] || {})[(isHiragana ? kana.hiragana : kana.katakana)]?.written)
    const pool = unmastered.length > 0 ? unmastered : allKana
    const nextKana = pool[Math.floor(Math.random() * pool.length)]
    setCurrentKana(nextKana)
    
    if (playSound) {
      // 停止當前播放的音檔
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause()
          currentAudioRef.current.currentTime = 0
          currentAudioRef.current = null
        } catch (e) {
          // 忽略錯誤
        }
        setIsPlaying(false)
      }
      
      setIsPlaying(true)
      const kanaText = isHiragana ? nextKana.hiragana : nextKana.katakana
      try {
        const { getDefaultTrialVoiceId } = await import('@/config/trial')
        const { getRomajiFromKana } = await import('@/utils/gojuonAudio')
        const voiceId = getDefaultTrialVoiceId()
        const romaji = getRomajiFromKana(kanaText)
        
        if (romaji) {
          const audioUrl = `https://tts.jlpt-aitutor.com/audio-files/gojuon/voice_${voiceId}/${romaji}.wav`
          const audio = new Audio(audioUrl)
          currentAudioRef.current = audio
          
          audio.addEventListener('ended', () => {
            setIsPlaying(false)
            currentAudioRef.current = null
          }, { once: true })
          
          audio.addEventListener('error', () => {
            setIsPlaying(false)
            currentAudioRef.current = null
            // 降級到 playGojuonAudio
            playGojuonAudio(
              kanaText,
              () => setIsPlaying(false),
              () => setIsPlaying(false),
              () => setIsPlaying(false),
              true
            )
          }, { once: true })
          
          await audio.play().catch(() => {
            // 播放失敗，降級到 playGojuonAudio
            playGojuonAudio(
              kanaText,
              () => setIsPlaying(false),
              () => setIsPlaying(false),
              () => setIsPlaying(false),
              true
            )
          })
        } else {
          // 沒有 romaji，使用 playGojuonAudio
          playGojuonAudio(
            kanaText,
            () => setIsPlaying(false),
            () => setIsPlaying(false),
            () => setIsPlaying(false),
            true
          )
        }
      } catch (error) {
        // 錯誤處理，使用 playGojuonAudio
        playGojuonAudio(
          kanaText,
          () => setIsPlaying(false),
          () => setIsPlaying(false),
          () => setIsPlaying(false),
          true
        )
      }
    }
  }, [progress, isHiragana])

  // 追蹤是否為初始載入
  const [hasUserInteracted, setHasUserInteracted] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // 初始化第一個假名（確保動畫能顯示）
  useEffect(() => {
    // 初始載入時不播放音頻（iOS 限制），但必須設置 currentKana 以顯示動畫
    if (!isInitialized) {
      selectNextKana(false)
      setIsInitialized(true)
    }
  }, [isInitialized, selectNextKana])

  // 切換假名類型時不自動跳轉，保持當前假名（只改變顯示方式）
  // 移除自動跳轉邏輯，讓用戶手動控制

  // 監聽用戶交互，標記為已交互
  useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true)
    }
    
    // 監聽各種用戶交互事件
    window.addEventListener('touchstart', handleUserInteraction, { once: true })
    window.addEventListener('click', handleUserInteraction, { once: true })
    
    return () => {
      window.removeEventListener('touchstart', handleUserInteraction)
      window.removeEventListener('click', handleUserInteraction)
    }
  }, [])

  const clearCanvas = () => {
    if (context && canvasRef.current) {
      context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    }
  }

  const handleComplete = () => {
    if (currentKana) {
      const kanaKey = isHiragana ? currentKana.hiragana : currentKana.katakana
      updateProgress(kanaKey, 'write', isHiragana ? 'hiragana' : 'katakana')
      setCompleted(prev => prev + 1)
      clearCanvas()
      setTimeout(() => selectNextKana(true), 500)
    }
  }

  // 滑鼠事件處理（觸摸事件已由 useEffect 中的原生監聽器處理）
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true)
    if (!context || !canvasRef.current) return
    
    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    context.beginPath()
    context.moveTo(x, y)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !context || !canvasRef.current) return
    
    const rect = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    context.lineTo(x, y)
    context.stroke()
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8">
      {/* 方案選擇彈窗 */}
      <PlanSelectionModal
        isOpen={showPlanModal}
        onClose={closePlanModal}
      />

      <div className="max-w-4xl mx-auto">
        {/* 試用版提示 */}
        <Card className="mb-6 border-yellow-500 bg-yellow-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold">50音完全免費</span>
                <span className="text-sm text-gray-300">N5~N1 學習內容需訂閱解鎖</span>
              </div>
              <Button
                onClick={openPlanModal}
                size="sm"
                className="bg-gradient-to-r from-purple-600 to-blue-600"
              >
                <Crown className="h-4 w-4 mr-2" />
                立即訂閱解鎖 N5~N1
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">書寫練習（試用版）</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="flex gap-4 mb-6 justify-center">
            <Button
              onClick={() => setIsHiragana(true)}
              variant={isHiragana ? 'selected' : 'default'}
            >
              平假名
            </Button>
            <Button
              onClick={() => setIsHiragana(false)}
              variant={!isHiragana ? 'selected' : 'default'}
            >
              片假名
            </Button>
          </div>

          {currentKana && (
            <Card className="mb-6">
              <CardContent className="p-8 text-center">
                <div className="mb-6">
                  <p className="text-6xl font-bold mb-4">
                    {isHiragana ? currentKana.hiragana : currentKana.katakana}
                  </p>
                  <Button
                    onClick={async () => {
                      // 停止當前播放的音檔
                      if (currentAudioRef.current) {
                        try {
                          currentAudioRef.current.pause()
                          currentAudioRef.current.currentTime = 0
                          currentAudioRef.current = null
                        } catch (e) {
                          // 忽略錯誤
                        }
                        setIsPlaying(false)
                      }
                      
                      setIsPlaying(true)
                      // 使用 playGojuonAudio 播放，但需要追蹤 Audio 對象
                      const kanaText = isHiragana ? currentKana.hiragana : currentKana.katakana
                      try {
                        const { getDefaultTrialVoiceId } = await import('@/config/trial')
                        const { getRomajiFromKana } = await import('@/utils/gojuonAudio')
                        const voiceId = getDefaultTrialVoiceId()
                        const romaji = getRomajiFromKana(kanaText)
                        
                        if (romaji) {
                          const audioUrl = `https://tts.jlpt-aitutor.com/audio-files/gojuon/voice_${voiceId}/${romaji}.wav`
                          const audio = new Audio(audioUrl)
                          currentAudioRef.current = audio
                          
                          audio.addEventListener('ended', () => {
                            setIsPlaying(false)
                            currentAudioRef.current = null
                          }, { once: true })
                          
                          audio.addEventListener('error', () => {
                            setIsPlaying(false)
                            currentAudioRef.current = null
                            // 降級到 playGojuonAudio
                            playGojuonAudio(
                              kanaText,
                              () => setIsPlaying(false),
                              () => setIsPlaying(false),
                              () => setIsPlaying(false),
                              true
                            )
                          }, { once: true })
                          
                          await audio.play().catch(() => {
                            // 播放失敗，降級到 playGojuonAudio
                            playGojuonAudio(
                              kanaText,
                              () => setIsPlaying(false),
                              () => setIsPlaying(false),
                              () => setIsPlaying(false),
                              true
                            )
                          })
                        } else {
                          // 沒有 romaji，使用 playGojuonAudio
                          playGojuonAudio(
                            kanaText,
                            () => setIsPlaying(false),
                            () => setIsPlaying(false),
                            () => setIsPlaying(false),
                            true
                          )
                        }
                      } catch (error) {
                        // 錯誤處理，使用 playGojuonAudio
                        playGojuonAudio(
                          kanaText,
                          () => setIsPlaying(false),
                          () => setIsPlaying(false),
                          () => setIsPlaying(false),
                          true
                        )
                      }
                    }}
                    disabled={isPlaying}
                    variant="secondary"
                    className="mb-4"
                  >
                    {isPlaying ? '播放中...' : '🔊 播放發音'}
                  </Button>
                </div>

                <div className="mb-4">
                  <KanaStrokeAnimation
                    kana={isHiragana ? currentKana.hiragana : currentKana.katakana}
                    type={isHiragana ? 'hiragana' : 'katakana'}
                    className="mx-auto w-64 h-64 sm:w-80 sm:h-80"
                  />
                </div>

                <div className="mb-4">
                  <canvas
                    ref={canvasRef}
                    width={canvasSize}
                    height={canvasSize}
                    className="border-2 border-white rounded-lg bg-gray-800 cursor-crosshair touch-none w-full max-w-md mx-auto"
                    style={{ touchAction: 'none', aspectRatio: '1' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                  />
                </div>

                <div className="flex gap-4 justify-center">
                  <Button 
                    onClick={clearCanvas}
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    清除
                  </Button>
                  <Button 
                    onClick={handleComplete} 
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    完成
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4">
              <div className="text-center">
                <p>已完成：{completed} 個</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 返回按鈕 - 頁面最後，靠左對齊 */}
        <div className="mt-6">
          <Button 
            onClick={() => router.push('/try/gojuon')}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            ← 返回
          </Button>
        </div>
      </div>
    </div>
  )
}

export default GojuonWriteTrialPage

